import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { APP_NAME } from "@/config";
import { Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Clock } from "lucide-react";

// A new company's setup, step by step (backend: company_routes.onboarding decides each step's state).
const STEPS = {
  runner: {
    title: "Your workspace server",
    waiting: "We're setting up the server your sessions run on, and we'll email you when it's ready. Nothing to do on your side.",
    installing: "Your server is being installed. We'll email you when it's ready.",
    setting_up: "Setting up your workspace server. It's usually ready within minutes; we'll email you when it is.",
    done: "Installed. Its status is below.",
  },
  api_key: {
    title: "Add your Anthropic or OpenAI API key",
    todo: `Sessions run on your own key. ${APP_NAME} shows what each one spends and stops it at your budgets.`,
    done: "Added.", to: "/founder/settings", action: "Add a key",
  },
  github: {
    title: "Connect GitHub",
    todo: "Choose the repositories sessions may work on. Each session gets its own branch and opens a pull request.",
    done: "Connected.", to: "/founder/settings", action: "Connect",
  },
  project: {
    title: "Create your first project",
    todo: "A project is one repository, with its base branch and the memory its sessions share.",
    done: "Created.", to: "/founder/projects", action: "New project",
  },
  coder: {
    title: "Invite someone",
    todo: "They get an email with a link to join.",
    waiting: "Invite sent. Waiting for them to join.",
    done: "Joined.", to: "/founder/team", action: "Invite",
  },
};

// Graphite only (it shows on the Overview): status colours, success blue on purpose, each icon next to its words
const ICONS = {
  done: <CheckCircle2 className="w-4 h-4 text-gr-success" />,
  waiting: <Clock className="w-4 h-4 text-gr-warning" />,
  todo: <Circle className="w-4 h-4 text-muted-foreground" />,
};

export default function GetStarted() {
  const { data } = useQuery({ queryKey: ["onboarding"], queryFn: async () => (await api.get("/onboarding")).data,
                              refetchInterval: 15000 });
  if (!data || data.done) return null;
  const done = data.steps.filter((s) => s.state === "done").length;
  return (
    <Card data-testid="get-started">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-h4 font-semibold">Get started</h2>
        <span className="text-sm text-muted-foreground tabular-nums">{done} of {data.steps.length} done</span>
      </div>
      <p className="text-sm text-muted-foreground mt-1 mb-4">What's left before your team can start their first session.</p>
      <ol className="divide-y border rounded-lg bg-background">
        {data.steps.map((s) => {
          const step = STEPS[s.key];
          if (!step) return null;
          const text = (s.detail && step[s.detail]) || step[s.state];
          return (
            <li key={s.key} className="flex items-center gap-3 px-4 py-3" data-testid={`onboarding-${s.key}`} data-state={s.state}>
              <span className="shrink-0">{ICONS[s.state]}</span>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold ${s.state === "done" ? "text-muted-foreground" : ""}`}>{step.title}</div>
                {text && <div className="text-sm text-muted-foreground mt-0.5">{text}</div>}
              </div>
              {s.state === "todo" && step.to && (
                <Button asChild size="sm" variant="outline" className="shrink-0"><Link to={step.to}>{step.action}</Link></Button>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
